import type {
	IBroker,
	IBrokerConfigParams,
	IBrokerProduct,
	IBrokerCreateRequest,
	IGridifyPaging,
	IPayment,
	IProduct,
	IResponseBase,
} from '@/interfaces';
import type { IBrokerResponse, IGridifyQuery } from '@/models';
import type {
	BrokerBillDTO,
	BrokerBillItemDTO,
	InvoiceReissuePreviewDTO,
	InvoiceReissueRequestDTO,
	InvoiceReissueResultDTO,
} from '@/models/dto/BrokerBill.dto';
import type { CancelTokenSource } from 'axios';
import { Http, HttpService } from './http';

const http = Http.create();

export class BrokerService extends HttpService {
	/**
	 * Return a list of brokers paginated based on query
	 * @param query
	 * @returns
	 */
	static async getBrokers(query: IGridifyQuery | null = null, cancelToken?: CancelTokenSource) {
		//const _cancelToken = cancelToken ?? this.createCancelToken('getBrokers');
		const response = await http.get<IGridifyPaging<IBrokerResponse>>('/brokers', {
			params: query,
			//cancelToken: _cancelToken,
		});

		this.cancelTokens['getBrokers'] = null;
		return response.data;
	}

	/**
	 * Cadastra o parceiro pela plataforma: nasce em "Pendente assinatura" e o Termo de Parceria segue pela ClickSign.
	 * Devolve o id do parceiro criado.
	 * @param brokerRequest
	 */
	static async addBroker(brokerRequest: IBrokerCreateRequest) {
		const response = await http.put<IResponseBase<string>>('/brokers', brokerRequest);

		return response.data;
	}

	/**
	 * Get a broker by id
	 * @param query
	 * @returns
	 */
	static async getBroker(brokerId: string) {
		const response = await http.get<IResponseBase<IBrokerResponse>>(`/brokers/${brokerId}`);
		return response.data;
	}

	/**
	 * Update broker data
	 * @param broker
	 * @returns
	 */
	static async updateBroker(broker: IBroker) {
		const response = await http.post<IResponseBase<IBroker>>(`/brokers/${broker.id}`, broker);
		return response.data;
	}

	/**
	 * Cancela o envelope do Termo de Parceria na ClickSign e envia um novo para o parceiro assinar.
	 * Só ADMIN, e só com o parceiro em "Pendente assinatura"; erro de negócio volta 400 com
	 * `errorMessage`.
	 * @param brokerId
	 */
	static async resendPartnershipTerm(brokerId: string) {
		const response = await http.post<IResponseBase<unknown>>(
			`/brokers/${brokerId}/partnership-term/resend`,
		);
		return response.data;
	}

	/**
	 * Return a list of broker products
	 * @param brokerId
	 * @returns
	 */
	static async getBrokerProducts(brokerId: string) {
		const cancelToken = this.createCancelToken('getBrokerProducts');

		const response = await http.get<IResponseBase<IBrokerProduct[]>>(
			`/brokers/${brokerId}/products`,
			{
				cancelToken,
			},
		);
		this.cancelTokens['getBrokerProducts'] = null;

		return response.data;
	}

	static async getBrokerBills(brokerId: string) {
		const cancelToken = this.createCancelToken('getBrokerBills');
		const response = await http.get<IResponseBase<BrokerBillDTO[]>>(`/brokers/${brokerId}/bills`, {
			cancelToken: cancelToken,
		});
		this.cancelTokens['getBrokerBills'] = null;
		return response.data;
	}

	static async getAllBrokerBills(brokerId: string, month: number, year: number) {
		const cancelToken = this.createCancelToken('getAllBrokerBills');
		const response = await http.get<IResponseBase<BrokerBillDTO[]>>(`/brokers/bills`, {
			params: {
				brokerId,
				month,
				year,
			},
			cancelToken: cancelToken,
		});
		this.cancelTokens['getAllBrokerBills'] = null;
		return response.data;
	}

	static async getBrokerBillItems(brokerId: string, month: number, year: number) {
		const cancelToken = this.createCancelToken('getBrokerBillItems');
		const response = await http.get<IResponseBase<BrokerBillItemDTO[]>>(
			`/brokers/${brokerId}/bills/${year}/${month}`,
			{ cancelToken: cancelToken },
		);
		this.cancelTokens['getBrokerBillItems'] = null;
		return response.data;
	}

	static async generateBrokerInvoice(brokerId: string, month: number, year: number) {
		const cancelToken = this.createCancelToken('generateBrokerInvoice');
		const response = await http.post<IResponseBase<IPayment>>(
			`/brokers/${brokerId}/bills/${year}/${month}/invoice`,
			null,
			{ cancelToken: cancelToken },
		);
		this.cancelTokens['generateBrokerInvoice'] = null;
		return response.data;
	}

	static async forceGenerateBrokerInvoice(brokerId: string, month: number, year: number) {
		const cancelToken = this.createCancelToken('generateBrokerInvoice');
		const response = await http.post<IResponseBase<IPayment>>(
			`/brokers/${brokerId}/bills/${year}/${month}/invoice/force`,
			null,
			{ cancelToken: cancelToken },
		);
		this.cancelTokens['generateBrokerInvoice'] = null;
		return response.data;
	}

	static async cancelBrokerInvoice(brokerId: string, invoiceId: string) {
		const cancelToken = this.createCancelToken('cancelBrokerInvoice');
		const response = await http.post<IResponseBase<IPayment>>(
			`/brokers/${brokerId}/invoices/${invoiceId}/cancel`,
			null,
			{ cancelToken: cancelToken },
		);
		this.cancelTokens['cancelBrokerInvoice'] = null;
		return response.data;
	}

	/** O que a reemissão da fatura faria: contratos que entram, saem e mudam, total e bloqueios. */
	static async getInvoiceReissuePreview(brokerId: string, year: number, month: number) {
		const response = await http.get<IResponseBase<InvoiceReissuePreviewDTO>>(
			`/brokers/${brokerId}/invoices/${year}/${month}/reissue/preview`,
		);
		return response.data;
	}

	/** Recalcula a fatura com os contratos vigentes, cancela o boleto atual e emite um novo. */
	static async reissueBrokerInvoice(brokerId: string, year: number, month: number, request: InvoiceReissueRequestDTO) {
		const response = await http.post<IResponseBase<InvoiceReissueResultDTO>>(
			`/brokers/${brokerId}/invoices/${year}/${month}/reissue`,
			request,
		);
		return response.data;
	}

	static GenerateBrokerInvoicePDF(brokerId: string, month: number, year: number) {
		return `${import.meta.env.VITE_API_SERVICE}/brokers/${brokerId}/invoices/${year}/${month}/pdf`;
	}

	static async createBrokerProduct(brokerId: string | string[], product: IBrokerProduct) {
		const cancelToken = this.createCancelToken('createBrokerProduct');
		const response = await http.post<IBrokerProduct>(`brokers/${brokerId}/products`, product, {
			cancelToken: cancelToken,
		});

		return response.data;
	}

	static async getParams(brokerId: string) {
		const cancelToken = this.createCancelToken('getParams');
		const response = await http.get<IResponseBase<IBrokerConfigParams>>(
			`/brokers/${brokerId}/params`,
			{ cancelToken },
		);
		this.cancelTokens['getParams'] = null;
		return response.data;
	}

	static async getAllProducts(query: IGridifyQuery | null = null, cancelToken?: CancelTokenSource) {
		const tokenToUse = cancelToken ? cancelToken.token : this.createCancelToken('getAllProducts');

		const response = await http.get<IGridifyPaging<IProduct>>('/product', {
			params: query,
			cancelToken: tokenToUse,
		});

		if (!cancelToken) {
			this.cancelTokens['getAllProducts'] = null;
		}

		return response.data;
	}

	/**
	 * Retorna produtos visíveis ao usuário, baseado na sua role (novo endpoint)
	 * @param query
	 * @param cancelToken
	 * @returns
	 */
	static async getVisibleProducts(
		query: IGridifyQuery | null = null,
		cancelToken?: CancelTokenSource,
	) {
		const tokenToUse = cancelToken
			? cancelToken.token
			: this.createCancelToken('getVisibleProducts');

		const response = await http.get<IGridifyPaging<IProduct>>('/product/visible', {
			params: query,
			cancelToken: tokenToUse,
		});

		if (!cancelToken) {
			this.cancelTokens['getVisibleProducts'] = null;
		}

        return response.data;
    }

	static async getProductById(productId: string){
		const cancelToken = this.createCancelToken('getProductById');

		const response = await http.get<IResponseBase<IBrokerProduct>>(
			`/product/${productId}`,
			{ cancelToken: cancelToken },
		);
		this.cancelTokens['getProductById'] = null;
		return response.data;
		
	}
}

export default BrokerService;
